import { AxiosResponse } from 'axios';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { setLoadingPage } from 'services/UI/sagas';
import { ISagaFunc } from 'services/actionConfigs';
import { IResponseDataBody } from 'services/response';
import shopActions from 'services/shop/actions';
import actions from './actions';
import apis from './apis';
import {
  ICreateCustomerBody,
  ISaveApiBodyType,
  ITicketDetails
} from './types/api';
import { ICustomerResItem } from './types/customer';
import { IDiscountTicketFormValue } from './types/discount';
import { ICustomerVIPPackage } from './types/vip';
import { checkChangeStateTicket, getPurePaymentInfo, getPureSelectedItems } from './selectors';
import { RootState } from 'store';
import { cashierUIActions } from './reducers';
import storage from 'utils/sessionStorage';
import { Message } from 'stompjs';
import { SHOP_DEVICE_TYPE } from 'services/shop/constants';
import { SocketConsentData, TypeSocketConsentPassData } from 'features/payment/pages/PaymentPage/services/types/socketConsent';
import { consentFormRef } from '../components/CenterMainBody/ModalConsentForm';

const getDetail: ISagaFunc<string> = function* ({ payload }) {
  const params: string = payload;
  yield setLoadingPage(true);
  yield delay(100);
  try {
    const resTicket: AxiosResponse<{ data: ITicketDetails }> = yield call(apis.getDetailsTicket, params);
    const resTicketDetail = resTicket?.data?.data;
    if (resTicketDetail) {
      yield put(actions.getTicketDetails.success(resTicketDetail));
      yield put(actions.checkCustomerPackage.fetch(resTicketDetail?.customerInfo?.customerId || ''));
      return;
    }
    const resBill: AxiosResponse<{ data: ITicketDetails }> = yield call(apis.getDetailBill, params);
    const resBillDetail = resBill?.data?.data;
    if (resBillDetail) {
      yield put(actions.getTicketDetails.success(resBillDetail));
      yield put(actions.checkCustomerPackage.fetch(resBillDetail?.customerInfo?.customerId || ''));
      return;
    }

  } catch (error) { } finally {
    yield setLoadingPage(false);
  }
};
const addNewCustomer: ISagaFunc<ICreateCustomerBody> = function* ({ payload }) {
  yield setLoadingPage(true);
  try {
    const res: IResponseDataBody<ICustomerResItem | null> = yield call(apis.addNewCustomer, payload);
    if (res.data.data) {
      yield put(actions.switchSelectedCustomer(res.data.data));
      if (payload.phoneReferral)
        yield put(actions.getCustomerReferral({ customerId: res.data.data.id, phoneReferral: payload.phoneReferral || '' }));
      yield put(shopActions.get.customers.fetch());
    }
  } catch (error) { }
  finally {
    yield setLoadingPage(false);
  }
};
const initData = function* ({ }) {
  yield all([
    put(shopActions.doInit.editTicket()),
  ]);
};

const calculateTotal = function* () {
  yield put(actions.calculateTicketPaymentInfo());
};

const checkCustomerPackage: ISagaFunc<string> = function* ({ payload }) {
  yield put(actions.checkCustomerPackage.success(null));
  if (!payload) return;
  yield setLoadingPage(true);
  try {
    const res: IResponseDataBody<ICustomerVIPPackage> = yield call(apis.getVIPPackageWithCustomerId, payload);
    const data = res?.data?.data;

    if (data) {
      const discountData: IDiscountTicketFormValue = {
        discount: data.discountValue,
        discountSetting: {
          ownerPaidPercent: 100,
          staffPaidPercent: 0
        },
        discountType: data.discountType,
        couponId: null,
        reason: ''
      };

      yield put(actions.setDiscountTicket(discountData));
      yield put(actions.checkCustomerPackage.success(data));
    } else {
      throw 'fail';
    }

  } catch (error) {
    yield put(actions.checkCustomerPackage.success(null));
  }
  finally {
    yield setLoadingPage(false);
  }
};

const switchSelectedCustomer: ISagaFunc<ICustomerResItem> = function* ({ payload }) {
  if (payload.accountPackage !== 'VIP') {
    yield put(actions.checkCustomerPackage.success(null));
    return;
  }

  yield put(actions.checkCustomerPackage.fetch(payload.id || ''));
};

const checkCashierWillUnmount = function* () {
  const pathName = location.pathname;
  if (!pathName.includes('ticket/edit')) return;

  const isChanged: boolean = yield select(checkChangeStateTicket);
  if (!isChanged) return;
  const paymentInfo: ISaveApiBodyType = yield select(getPurePaymentInfo);
  if (paymentInfo.items.length === 0) {
    return null;
  }

  try {
    yield call(apis.editTicketBackground, paymentInfo);
  } catch (error) { }
};

const checkClearCustomer = function* () {
  const items: RootState['cashier']['selectedItems'] = yield select(getPureSelectedItems);
  const existVIPPackage = items.some(o => !!o.services.find(s => s.itemType === 'VIP'));
  if (existVIPPackage) {
    yield put(actions.setVisibleModalWarningVIPPackage(true));
    return;
  }
  yield put(actions.clearCustomer.success({}));
};

const setCountStaffInRowUI: ISagaFunc<number> = function* ({ payload }) {
  yield storage.count_staff_in_row_cashier.set(payload?.toString());
};

export const checkExistsCountStaffInRowUI = function* () {
  const local: string = yield storage.count_staff_in_row_cashier.get();
  const count_staff_in_row_cashier: number = yield parseInt(local);
  yield put(cashierUIActions.setCountStaffInRowUI(count_staff_in_row_cashier || 4));
};

const getCustomerReferral: ISagaFunc<{ customerId: string, phoneReferral: string }> = function* ({ payload }) {
  try {
    const res: IResponseDataBody<any> = yield call(apis.checkReferralSender, payload.customerId, payload.phoneReferral);
    if (res.data.data) {
      yield put(cashierUIActions.saveCustomerReferral(res.data.data));
    }
  } catch (error) {
  }
};

const syncCustomerConsentFrontSocket: ISagaFunc<Message['body']> = function* ({ payload }) {
  const body: { customerId: string, device_target: string, billId: string } = JSON.parse(payload || '');
  if (!body.customerId) return;
  if (body.device_target !== SHOP_DEVICE_TYPE.POS) return;
  const ticketDetail: ITicketDetails = yield select((state: RootState) => state.cashier.ticketDetails);
  if (!ticketDetail) return;
  if (ticketDetail.customerInfo?.customerId !== body.customerId) return;
  try {
    const resBill: AxiosResponse<{ data: ITicketDetails }> = yield call(apis.getDetailBill, ticketDetail.ticketInfo.billId || '');
    const resBillDetail = resBill?.data?.data;
    if (resBillDetail) {
      yield put(actions.syncCustomerConsentFrontSocket.success(resBillDetail));
      return;
    }
  } catch (error) {
  }
};

const setConsentMsg: ISagaFunc<Message['body']> = function* ({ payload }) {
  const body: SocketConsentData = JSON.parse(payload || '');
  if (!body.customerId) return;
  if (body.action !== TypeSocketConsentPassData.REQUEST_CONSENT) {
    yield put(actions.setConsentMsg.success(payload));
    return;
  }
  yield setLoadingPage(true);
  try {
    yield consentFormRef.current?.retrieveCustomerId(body.customerId);
  } catch (error) {
  } finally {
    yield setLoadingPage(false);
  }
};

export default function* cashierServiceSagas() {
  yield takeLatest(actions.initData, initData);
  yield takeLatest(actions.getDetail.fetch, getDetail);

  yield takeLatest(actions.addNewCustomer.fetch, addNewCustomer);

  yield takeLatest(actions.selectSimpleMenuServiceItem, calculateTotal);
  yield takeLatest(actions.selectServiceItem, calculateTotal);
  yield takeLatest(actions.addStaffItem, calculateTotal);
  yield takeLatest(actions.switchStaffItem, calculateTotal);
  yield takeLatest(actions.removeServiceOfItem, calculateTotal);
  yield takeLatest(actions.switchSelectedCustomer, calculateTotal);
  yield takeLatest(actions.removeSelectedCustomer, calculateTotal);
  yield takeLatest(actions.setTip, calculateTotal);
  yield takeLatest(actions.setDiscountTicket, calculateTotal);
  yield takeLatest(actions.setDiscountItem, calculateTotal);
  yield takeLatest(actions.addQuickPay, calculateTotal);
  yield takeLatest(actions.addGiftCard, calculateTotal);
  yield takeLatest(actions.clearTicket, calculateTotal);
  yield takeLatest(actions.selectProductItem, calculateTotal);
  yield takeLatest(actions.selectProductVIPItem, calculateTotal);
  yield takeLatest(actions.updateQuickPayItem, calculateTotal);
  yield takeLatest(actions.removeStaffItem, calculateTotal);
  yield takeLatest(actions.switchSelectedCustomer, switchSelectedCustomer);
  yield takeLatest(actions.checkCustomerPackage.fetch, checkCustomerPackage);
  yield takeLatest(actions.checkCashierWillUnmount, checkCashierWillUnmount);
  yield takeLatest(actions.clearCustomer.fetch, checkClearCustomer);
  yield takeLatest(cashierUIActions.setCountStaffInRowUI, setCountStaffInRowUI);
  yield takeLatest(actions.getCustomerReferral, getCustomerReferral);
  yield takeLatest(actions.updateStaffTurnStatus.success, getDetail);
  yield takeLatest(actions.syncCustomerConsentFrontSocket.fetch, syncCustomerConsentFrontSocket);
  yield takeLatest(actions.setConsentMsg.fetch, setConsentMsg);
}
